import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { PROCESS_STEPS, WHY_US, STATS } from "@/data/content";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display text-5xl md:text-6xl font-light text-[#d4af37]">
      {display}
      {suffix}
    </span>
  );
};

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="mb-16">
          <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] font-medium mb-6">
            How It Works
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-tight text-slate-50">
            From dream to <span className="italic text-[#d4af37]">departure.</span>
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#d4af37]/15 border border-[#d4af37]/15">
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08} className="bg-[#0A1128]">
              <div className="p-8 h-full hover:bg-[#101C3E]/60 transition-colors duration-500" data-testid={`process-step-${i}`}>
                <span className="font-display text-5xl font-light text-[#d4af37]/40">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-2xl font-medium tracking-tight text-slate-50 mt-6 mb-3">
                  {s.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-14 mt-24 items-start">
          <Reveal>
            <h3 className="font-display text-3xl md:text-4xl font-light tracking-tight text-slate-50 mb-8">
              Why travellers choose{" "}
              <span className="italic text-[#d4af37]">RK.</span>
            </h3>
            <ul className="space-y-5">
              {WHY_US.map((w, i) => (
                <li key={i} className="flex items-start gap-4" data-testid={`why-us-item-${i}`}>
                  <span className="mt-1 w-5 h-5 border border-[#d4af37] flex items-center justify-center shrink-0">
                    <Check size={12} className="text-[#d4af37]" />
                  </span>
                  <span className="text-slate-300 leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-px bg-[#d4af37]/15 border border-[#d4af37]/15">
              {STATS.map((s, i) => (
                <div
                  key={s.label}
                  data-testid={`stat-${i}`}
                  className="bg-[#101C3E]/50 p-8 flex flex-col gap-2"
                >
                  <Counter value={s.value} suffix={s.suffix} />
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-400">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
